import React,{useState,useEffect,useCallback}from'react';
import{useNavigate}from'react-router-dom';
import Layout from'../components/layout/Layout';

const INP=({value,onChange,type='text',placeholder='',disabled=false}:any)=>(
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} disabled={disabled}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#015CA6] disabled:bg-gray-50"/>
);
const SEL=({value,onChange,children}:any)=>(
  <select value={value} onChange={onChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
    {children}
  </select>
);
const FLD=({label,children,required}:any)=>(
  <div><label className="block text-xs font-semibold text-gray-600 mb-1">{label}{required&&<span className="text-red-500 ml-0.5">*</span>}</label>{children}</div>
);
const token=()=>localStorage.getItem('accessToken')||localStorage.getItem('token')||'';
const hdr=()=>({'Content-Type':'application/json','Authorization':`Bearer ${token()}`});
const BASE=process.env.REACT_APP_API_URL;

async function api(path:string,method='GET',body?:any){
  const r=await fetch(`${BASE}${path}`,{method,headers:hdr(),body:body?JSON.stringify(body):undefined});
  return r.json().catch(()=>({}));
}

function MasterSection({title,icon,endpoint,fields,keyField='name'}:{title:string;icon:string;endpoint:string;fields:{key:string;label:string;type?:string;options?:string[]}[];keyField?:string}){
  const [loading,setLoading]=useState(true);
  const [items,setItems]=useState<any[]>([]);
  const [form,setForm]=useState<any>({});
  const [editId,setEditId]=useState<string|null>(null);
  const [msg,setMsg]=useState('');
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),2500);};

  const load=useCallback(async()=>{
    setLoading(true);
    const resp=await api(endpoint);
    setItems(Array.isArray(resp)?resp:resp?.data||[]);
    setLoading(false);
  },[endpoint]);

  useEffect(()=>{load();},[load]);

  const defForm=Object.fromEntries(fields.map(f=>[f.key,'']));

  const save=async()=>{
    if(!form[keyField])return notify('❌ Required field missing');
    const r=editId?await api(`${endpoint}/${editId}`,'PUT',form):await api(endpoint,'POST',form);
    if(r?.id||r?.data?.id||Array.isArray(r)){notify(editId?'✅ Updated':'✅ Created');setForm({...defForm});setEditId(null);load();}
    else notify('❌ Failed — check backend');
  };

  const del=async(id:string)=>{
    if(!window.confirm('Delete?'))return;
    await fetch(`${BASE}${endpoint}/${id}`,{method:'DELETE',headers:hdr()});
    notify('✅ Deleted');load();
  };

  return(
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b" style={{background:'linear-gradient(135deg,#02357A,#015CA6)'}}>
        <h3 className="font-bold text-white text-sm">{icon} {title}</h3>
        <span className="text-white/60 text-xs">{items.length} records</span>
      </div>
      {msg&&<div className={`px-4 py-2 text-xs font-medium ${msg.startsWith('✅')?'bg-green-50 text-green-700':'bg-red-50 text-red-700'}`}>{msg}</div>}
      {/* Form */}
      <div className="p-4 border-b bg-gray-50">
        <div className="grid grid-cols-2 gap-2 mb-2">
          {fields.map(f=>(
            <FLD key={f.key} label={f.label}>
              {f.options?(
                <SEL value={form[f.key]||''} onChange={(e:any)=>setForm((x:any)=>({...x,[f.key]:e.target.value}))}>
                  <option value="">Select</option>{f.options.map(o=><option key={o}>{o}</option>)}
                </SEL>
              ):(
                <INP type={f.type||'text'} value={form[f.key]||''} onChange={(e:any)=>setForm((x:any)=>({...x,[f.key]:e.target.value}))} placeholder={f.label}/>
              )}
            </FLD>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={save} className="px-4 py-1.5 rounded-lg text-xs font-bold text-white" style={{background:'#02357A'}}>{editId?'Update':'+ Add'}</button>
          {editId&&<button onClick={()=>{setForm({...defForm});setEditId(null);}} className="px-3 py-1.5 rounded-lg text-xs border hover:bg-gray-100">Cancel</button>}
        </div>
      </div>
      {/* List */}
      <div className="max-h-60 overflow-y-auto divide-y">
        {items.length===0&&<p className="text-center py-6 text-xs text-gray-400">No records yet</p>}
        {items.map((item:any)=>(
          <div key={item.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{item[keyField]||item.name||item.code||'—'}</p>
              {item.description&&<p className="text-xs text-gray-400 truncate">{item.description}</p>}
              {item.code&&item.name&&<p className="text-xs text-gray-400">{item.code}</p>}
            </div>
            <div className="flex gap-1 flex-shrink-0 ml-2">
              <button onClick={()=>{const f:any={};fields.forEach(fl=>f[fl.key]=item[fl.key]||'');setForm(f);setEditId(item.id);}} className="px-2 py-1 rounded text-xs bg-gray-100 hover:bg-gray-200">✏️</button>
              <button onClick={()=>del(item.id)} className="px-2 py-1 rounded text-xs bg-red-50 text-red-500 hover:bg-red-100">🗑️</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const MASTER_SECTIONS=[
  {title:'Departments',icon:'🏥',endpoint:'/settings/departments',keyField:'name',fields:[{key:'name',label:'Department Name'},{key:'department_code',label:'Code'},{key:'description',label:'Description'}]},
  {title:'Designations',icon:'👤',endpoint:'/hr/designations',keyField:'designationName',fields:[{key:'designationName',label:'Designation'},{key:'designationCode',label:'Code'},{key:'grade',label:'Category',options:['DOCTOR','NURSE','TECHNICIAN','ADMINISTRATIVE','SUPPORT','MANAGEMENT']}]},
  {title:'Shift Master',icon:'🕐',endpoint:'/hr/shifts',keyField:'shiftName',fields:[{key:'shiftName',label:'Shift Name'},{key:'shiftCode',label:'Code'},{key:'startTime',label:'Start Time',type:'time'},{key:'endTime',label:'End Time',type:'time'},{key:'shiftType',label:'Type',options:['MORNING','EVENING','NIGHT','GENERAL','SPLIT']}]},
  {title:'Leave Types',icon:'🏖️',endpoint:'/hr/leave-types',keyField:'leaveName',fields:[{key:'leaveName',label:'Leave Type'},{key:'leaveCode',label:'Code'},{key:'annualQuota',label:'Days/Year',type:'number'}]},
  {title:'Holidays',icon:'🎉',endpoint:'/settings/holidays',keyField:'holiday_name',fields:[{key:'holiday_name',label:'Holiday Name'},{key:'holiday_date',label:'Date',type:'date'},{key:'holiday_type',label:'Type',options:['NATIONAL','REGIONAL','OPTIONAL','RESTRICTED']}]},
  {title:'Number Series',icon:'🔢',endpoint:'/settings/number-series',keyField:'series_name',fields:[{key:'series_name',label:'Series Name'},{key:'prefix',label:'Prefix'},{key:'suffix',label:'Suffix'},{key:'starting_number',label:'Start No.',type:'number'},{key:'padding',label:'Digits',type:'number'},{key:'module',label:'Module',options:['PATIENT','IPD','OPD','LAB','RADIOLOGY','BILLING','PHARMACY','BLOOD_BANK','OT','EMERGENCY']}]},
  {title:'Room Categories',icon:'🚪',endpoint:'/settings/masters/room-categories',keyField:'name',fields:[{key:'name',label:'Category Name'},{key:'base_rate',label:'Rate/Day ₹',type:'number'},{key:'description',label:'Description'}]},
  {title:'Bed Categories',icon:'🛏️',endpoint:'/settings/masters/bed-categories',keyField:'name',fields:[{key:'name',label:'Category Name'},{key:'category_code',label:'Code'},{key:'base_rate',label:'Rate/Day ₹',type:'number'}]},
  {title:'Service Categories',icon:'💰',endpoint:'/settings/masters/service-categories',keyField:'name',fields:[{key:'name',label:'Category Name'},{key:'category_code',label:'Code'},{key:'gst_percent',label:'Default GST %',type:'number'}]},
  {title:'Drug Categories',icon:'💊',endpoint:'/settings/masters/drug-classes',keyField:'name',fields:[{key:'name',label:'Drug Class'},{key:'class_code',label:'Code'},{key:'schedule',label:'Schedule',options:['H','H1','X','G','OTC','NARCOTIC']}]},
  {title:'Lab Sections',icon:'🧪',endpoint:'/laboratory/sections',keyField:'name',fields:[{key:'name',label:'Section Name'},{key:'code',label:'Code'},{key:'sectionType',label:'Type',options:['BIOCHEMISTRY','HAEMATOLOGY','MICROBIOLOGY','SEROLOGY','HISTOPATHOLOGY','CYTOLOGY','OTHER']}]},
  {title:'Radiology Modalities',icon:'📡',endpoint:'/radiology/modalities',keyField:'modality_name',fields:[{key:'modality_name',label:'Modality'},{key:'modality_code',label:'Code'},{key:'description',label:'Description'}]},
  {title:'Blood Groups',icon:'🩸',endpoint:'/settings/blood-groups',keyField:'blood_group',fields:[{key:'blood_group',label:'Blood Group'},{key:'is_rare',label:'Rare?',options:['false','true']}]},
  {title:'Relationship Types',icon:'👨‍👩‍👧',endpoint:'/settings/relationships',keyField:'relation_name',fields:[{key:'relation_name',label:'Relation'},{key:'relation_code',label:'Code'}]},
  {title:'Document Types',icon:'📄',endpoint:'/dms/document-types',keyField:'type_name',fields:[{key:'type_name',label:'Document Type'},{key:'type_code',label:'Code'},{key:'retention_years',label:'Retain (years)',type:'number'},{key:'requires_approval',label:'Needs Approval?',options:['false','true']}]},
  {title:'Notification Events',icon:'🔔',endpoint:'/enterprise/notification-events',keyField:'event_code',fields:[{key:'event_code',label:'Event Code'},{key:'event_name',label:'Event Name'},{key:'module',label:'Module',options:['OPD','IPD','LAB','RADIOLOGY','PHARMACY','BILLING','EMERGENCY','BLOOD_BANK','HR']},{key:'default_channel',label:'Default Channel',options:['SMS','EMAIL','WHATSAPP','IN_APP','ALL']}]},
  {title:'OT Rooms',icon:'🔬',endpoint:'/ot/rooms',keyField:'room_name',fields:[{key:'room_name',label:'Room Name'},{key:'room_code',label:'Code'},{key:'room_type',label:'Type',options:['MAJOR','MINOR','ENDOSCOPY','CARDIAC','NEURO','ORTHO','EMERGENCY']},{key:'floor_number',label:'Floor',type:'number'}]},
  {title:'Equipment Categories',icon:'⚙️',endpoint:'/biomedical/categories',keyField:'category_name',fields:[{key:'category_name',label:'Category'},{key:'category_code',label:'Code'},{key:'pm_frequency_months',label:'PM Frequency (months)',type:'number'}]},
  {title:'CSSD Instrument Categories',icon:'🧰',endpoint:'/enterprise/cssd/instruments',keyField:'instrument_name',fields:[{key:'instrument_name',label:'Instrument Name'},{key:'instrument_code',label:'Code'},{key:'category',label:'Category',options:['SURGICAL','DIAGNOSTIC','LAPAROSCOPIC','ORTHOPAEDIC','GYNAECOLOGY','ENT']},{key:'material',label:'Material',options:['STAINLESS_STEEL','TITANIUM','CARBON_FIBRE','PLASTIC']}]},
  {title:'Diet Types',icon:'🥗',endpoint:'/settings/diet-types',keyField:'diet_name',fields:[{key:'diet_name',label:'Diet Type'},{key:'diet_code',label:'Code'},{key:'description',label:'Description'},{key:'calorie_count',label:'Calories',type:'number'}]},
];

export default function MasterDataPage(){
  const nav=useNavigate();
  const [search,setSearch]=useState('');
  const filtered=MASTER_SECTIONS.filter(s=>s.title.toLowerCase().includes(search.toLowerCase()));

  return(
    <Layout title="System Master Data" actions={
      <div className="flex gap-2">
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search masters..." className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none w-48"/>
        <button onClick={()=>nav('/settings')} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">← Settings</button>
      </div>
    }>
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {filtered.map(s=>(
            <MasterSection key={s.endpoint} {...s}/>
          ))}
        </div>
        {filtered.length===0&&<div className="text-center py-16 text-gray-400"><p className="text-4xl mb-3">🔍</p><p>No masters found for "{search}"</p></div>}
      </div>
    </Layout>
  );
}
